import { useState } from "react";
import { Badge, Card, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { useAdminResource } from "../hooks";
import { ActionNotice, useDialogs } from "../dialogs";
import { DataTable, DataRow, EmptyHint, ErrorState, LoadingState } from "../components";
import { isOverdue, shortId } from "../format";
import type { PrivacyRequestRow } from "../types";

export function PrivacyPage() {
  const { t, formatDate } = useI18n();
  const dialogs = useDialogs();
  const requests = useAdminResource<PrivacyRequestRow[]>("/privacy-requests");
  const [notice, setNotice] = useState<{ error?: string; info?: string }>({});

  const typeOf = (type: string) =>
    type === "access" ? t("admin.privacy.typeAccess")
      : type === "export" ? t("admin.privacy.typeExport")
        : type === "correction" ? t("admin.privacy.typeCorrection")
          : type === "deletion" ? t("admin.privacy.typeDeletion")
            : type === "restriction" ? t("admin.privacy.typeRestriction")
              : type === "objection" ? t("admin.privacy.typeObjection")
                : type;

  const statusBadge = (status: string): { tone: "coral" | "amber" | "green" | "neutral"; label: string } => {
    if (status === "open") return { tone: "coral", label: t("admin.privacy.statusOpen") };
    if (status === "review") return { tone: "amber", label: t("admin.privacy.statusReview") };
    if (status === "in_progress") return { tone: "amber", label: t("admin.privacy.statusInProgress") };
    if (status === "completed") return { tone: "green", label: t("admin.privacy.statusCompleted") };
    return { tone: "neutral", label: t("admin.privacy.statusCompleted") };
  };

  const resolve = async (item: PrivacyRequestRow) => {
    const confirmed = await dialogs.confirm({
      title: t("admin.privacy.resolveTitle"),
      message: t("admin.privacy.resolveMessage", { id: shortId(t, item.id), type: typeOf(item.type) }),
      confirmLabel: t("admin.privacy.resolveConfirm")
    });
    if (!confirmed) return;
    try {
      await adminApi(`/privacy-requests/${item.id}/resolve`, { method: "POST" });
      setNotice({ info: t("admin.privacy.resolvedNotice") });
      requests.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const rows = requests.data ?? [];
  const labels = [
    t("admin.privacy.colRequest"),
    t("admin.users.colUser"),
    t("admin.privacy.colReceived"),
    t("admin.privacy.colDue"),
    t("admin.status"),
    t("admin.privacy.colActions")
  ];
  const columns = [
    { label: labels[0], width: "minmax(180px, 1.2fr)" },
    { label: labels[1], width: "minmax(120px, .8fr)" },
    { label: labels[2], width: "minmax(120px, .9fr)" },
    { label: labels[3], width: "minmax(140px, .9fr)" },
    { label: labels[4], width: "minmax(110px, .8fr)" },
    { label: labels[5], width: "minmax(150px, .9fr)" }
  ];

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.privacy.title")} subtitle={t("admin.privacy.subtitle")} />
      <ActionNotice error={notice.error} info={notice.info} onDismiss={() => setNotice({})} />

      {requests.status === "error" && <ErrorState message={requests.error} onRetry={() => void requests.reload()} />}
      {requests.status === "loading" && <LoadingState rows={3} />}

      {requests.status === "ready" && (
        rows.length === 0
          ? <EmptyHint>{t("admin.privacy.empty")}</EmptyHint>
          : (
            <DataTable columns={columns} minWidth={840}>
              {rows.map((item) => {
                const badge = statusBadge(item.status);
                const overdue = isOverdue(item.dueAt, item.status);
                return (
                  <DataRow key={item.id} labels={labels}>
                    <span className="admin-request">
                      <strong>{typeOf(item.type)}</strong>
                      <small>{shortId(t, item.id)}</small>
                    </span>
                    <span className="admin-time">{shortId(t, item.userId)}</span>
                    <span className="admin-time">{formatDate(item.createdAt)}</span>
                    <span className="admin-time">
                      {formatDate(item.dueAt)}
                      {overdue && <Badge tone="coral">{t("admin.privacy.overdue")}</Badge>}
                    </span>
                    <Badge tone={badge.tone}>{badge.label}</Badge>
                    <span>
                      {item.status !== "completed" && (
                        <button type="button" className="privacy-resolve" onClick={() => void resolve(item)}>
                          <Icon name="check" size={14} />{t("admin.privacy.statusCompleted")}
                        </button>
                      )}
                    </span>
                  </DataRow>
                );
              })}
            </DataTable>
          )
      )}

      <Card className="compliance-card">
        <Icon name="shield" size={28} />
        <div>
          <h2>{t("admin.privacy.privacyByDefault")}</h2>
          <p>{t("admin.privacy.complianceText")}</p>
        </div>
      </Card>
    </div>
  );
}
